import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';
import { projectDetails, projectMembersListInfo, siteWiseDailyReportsSummary } from '../../../../../../core/projects.selectors';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../../../../../../state/app.state';
import { MapService } from '../../../../../../../../shared/services/map.service';
import {
  FetchProjectMembersListRequest,
  FetchSiteWiseDailyReportSummaryRequest,
} from '../../../../../../core/projects.actions';
import { AuthService } from '../../../../../../../../shared/services';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { ProjectDetailsService } from '../../project-details.service';

const MY_TEAM_CREATOR_FILTER = '__my_team__';

@Component({
    selector: 'app-site-wise-reports',
    templateUrl: './site-wise-reports.component.html',
    styleUrls: ['./site-wise-reports.component.scss'],
    standalone: false
})
export class SiteWiseReportsComponent implements OnInit, OnDestroy {
  sites: any[] = [];
  filteredSites: any[] = [];
  allProjectSites: any[] = [];
  startDate = null;
  endDate = null;
  projectDetails: any;
  projectMembers: any[] = [];
  myTeamAccountIds: string[] = [];
  siteIdsToFilter: string[] = [];
  statusIdsToFilter: string[] = [];
  creatorIdsToFilter: string[] = [];
  itemsPerPage = 5;
  currentPage = 1;
  paginatedFilteredSites: any[];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private mapService: MapService,
    private commonService: CommonService,
    private authService: AuthService,
    private projectDetailsService: ProjectDetailsService,
  ) {

  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.startDate && moment(qp.startDate).isValid()) {
          this.startDate = qp.startDate;
        } else {
          this.startDate = null;
        }

        if (qp.endDate && moment(qp.endDate).isValid()) {
          this.endDate = qp.endDate;
        } else {
          this.endDate = null;
        }

        if (qp.status) {
          if (Array.isArray(qp.status)) {
            this.statusIdsToFilter = qp.status;
          } else {
            this.statusIdsToFilter = [qp.status];
          }
        } else {
          this.statusIdsToFilter = [];
        }

        if (qp.site) {
          if (Array.isArray(qp.site)) {
            this.siteIdsToFilter = qp.site;
          } else {
            this.siteIdsToFilter = [qp.site];
          }
        } else {
          this.siteIdsToFilter = [];
        }

        if (qp.creator) {
          if (Array.isArray(qp.creator)) {
            this.creatorIdsToFilter = qp.creator;
          } else {
            this.creatorIdsToFilter = [qp.creator];
          }
        } else {
          this.creatorIdsToFilter = [];
        }

        this.filterSites();
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (Object.keys(data).length !== 0) {
          this.projectDetails = data;
          this.allProjectSites = data.sites || [];
          this.updateMapWithAllSites();
          this.filterSites();
        }
      });

    this.store.select(projectMembersListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.projectMembers = data?.list || [];
        this.updateMyTeamAccountIds();
        this.filterSites();
      });

    this.store.dispatch(FetchSiteWiseDailyReportSummaryRequest({
      payload: {
        projectId: this.route.snapshot.params.projectId,
      },
    }));
    this.store.dispatch(FetchProjectMembersListRequest({
      payload: {
        projectId: this.route.snapshot.params.projectId,
      },
    }));

    this.store.select(siteWiseDailyReportsSummary)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        this.sites = data;
        if (this.sites.length) {
          this.filterSites();
        }
      });

    this.commonService.resetPaginator
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.currentPage = 1;
      });
  }

  filterSites() {
    let filteredSites = JSON.parse(JSON.stringify(this.sites));

    filteredSites = filteredSites.filter(site => {
      if (this.siteIdsToFilter.length) {
        return this.siteIdsToFilter.includes(site.site_id);
      } else {
        return true;
      }
    })
      .filter(site => {
        if (this.statusIdsToFilter.length || this.creatorIdsToFilter.length) {
          site.daily_reports = site.daily_reports
            .filter(report => {
              if (this.statusIdsToFilter.length) {
                const status = JSON.parse(JSON.stringify(this.statusIdsToFilter));

                const index = this.statusIdsToFilter.indexOf('submitted-reviewed');

                if (index > -1) {
                  status.splice(index, 1);
                  status.push(...['submitted', 'reviewed']);
                }

                return status.includes(report.status);
              }
              return true;
            })
            .filter(report => {
              const creatorIdsToFilter = this.getCreatorIdsToFilter();
              if (creatorIdsToFilter.length) {
                return creatorIdsToFilter.includes(report.created_by_id);
              }

              return true;
            }) || [];

          if (site.daily_reports.length) {
            return true;
          }
        } else {
          return true;
        }

      });

    if (this.startDate) {
      filteredSites = filteredSites
        .filter(site => {
          site.daily_reports = site?.daily_reports?.filter(o => moment(o.report_date, 'YYYY-MM-DD')
            .isSameOrAfter(moment(this.startDate, 'YYYY-MM-DD'))) || [];

          if (site.daily_reports.length) {
            return site;
          }
        },
        );
    }


    if (this.endDate) {
      filteredSites = filteredSites
        .filter(site => {
          site.daily_reports = site?.daily_reports?.filter(o => moment(o.report_date, 'YYYY-MM-DD')
            .isSameOrBefore(moment(this.endDate, 'YYYY-MM-DD')),
          ) || [];

          if (site.daily_reports.length) {
            return site;
          }
        });
    }

    filteredSites = filteredSites
      .filter(o => o?.daily_reports?.length)
      .sort((a, b) => {
        const lastUpdatedA = Math.max(...a.daily_reports?.map(report => new Date(report.updated_at).getTime()));
        const lastUpdatedB = Math.max(...b.daily_reports?.map(report => new Date(report.updated_at).getTime()));

        return lastUpdatedB - lastUpdatedA;
      });

    this.filteredSites = filteredSites;
    this.projectDetailsService.filteredDailyReportIds.next(
      filteredSites.flatMap(site => (site.daily_reports || []).map(report => report.daily_report_id)),
    );
    this.onPageChanged(1);
    this.commonService.resetPaginator.next(true);
    this.updateMapWithAllSites();
  }

  updateMapWithAllSites() {
    // Skip if site filter applied but filteredSites is still empty (premature call)
    if (this.siteIdsToFilter.length && !this.filteredSites.length) {
      return;
    }

    // Ensure that project sites AND the base report summary sites are loaded
    if (!this.allProjectSites.length || this.filteredSites === undefined) {
      return;
    }

    // Create a set of filtered site IDs for quick lookup
    const filteredSiteIds = new Set(this.filteredSites.map(site => site.site_id));


    // Enhance all project sites with filtering information
    const enhancedSites = this.allProjectSites.map(projectSite => {
      const isFiltered = filteredSiteIds.has(projectSite.id);
      const filteredSiteData = this.filteredSites.find(site => site.site_id === projectSite.id);

      return {
        ...projectSite,
        isFiltered,
        // Include daily reports data if this site is filtered
        daily_reports: isFiltered ? filteredSiteData?.daily_reports || [] : [],
        // Map project site fields to match expected format
        site_id: projectSite.id,
        site_name: projectSite.name,
        longitude: projectSite.longitude,
        latitude: projectSite.latitude,
        last_updated_at: filteredSiteData?.last_updated_at || projectSite.updated_at
      };
    }).filter(site => site.longitude && site.latitude); // Only include sites with coordinates

    // NOTE: Geometry management is now handled by MapLayerContainerComponent
    // Don't send siteReportGeometries from here - it would clear geometries set by the map container

    // Send all enhanced sites to the map (sites data only, not geometries)
    this.mapService.initiateSiteLocationsMap.next(enhancedSites);
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.updateFilteredSitesList();
  }

  updateFilteredSitesList(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredSites.length);

    this.paginatedFilteredSites = this.filteredSites.slice(startIndex, endIndex);
    // Note: We don't send paginatedFilteredSites to map anymore since we're sending all sites
  }

  private getCreatorIdsToFilter(): string[] {
    if (!this.creatorIdsToFilter.length) {
      return [];
    }

    if (!this.creatorIdsToFilter.includes(MY_TEAM_CREATOR_FILTER)) {
      return this.creatorIdsToFilter;
    }

    if (!this.isDwrReportingHierarchyEnabled()) {
      return this.creatorIdsToFilter.filter(id => id !== MY_TEAM_CREATOR_FILTER);
    }

    if (this.myTeamAccountIds.length) {
      return this.myTeamAccountIds;
    }

    const currentUser: any = this.authService?.getCurrentUser();
    const currentUserId = currentUser?.id || currentUser?.uuid;
    return currentUserId ? [currentUserId] : [];
  }

  private updateMyTeamAccountIds(): void {
    const currentUser: any = this.authService?.getCurrentUser();
    const currentUserId = currentUser?.id || currentUser?.uuid;

    if (!currentUserId) {
      this.myTeamAccountIds = [];
      return;
    }

    const reportsByManager = this.projectMembers.reduce((acc: Record<string, any[]>, member: any) => {
      const managerId = member?.reports_to?.uuid || member?.reports_to_account_id;
      if (managerId) {
        acc[managerId] = acc[managerId] || [];
        acc[managerId].push(member);
      }
      return acc;
    }, {});

    const teamIds = new Set<string>([currentUserId]);
    const visit = (accountId: string): void => {
      (reportsByManager[accountId] || []).forEach((member: any) => {
        const memberId = member?.uuid || member?.id;
        if (memberId && !teamIds.has(memberId)) {
          teamIds.add(memberId);
          visit(memberId);
        }
      });
    };

    visit(currentUserId);
    this.myTeamAccountIds = Array.from(teamIds);
  }

  private isDwrReportingHierarchyEnabled(): boolean {
    return this.projectDetails?.source_entity_extra_attributes?.dwr_reporting_hierarchy_enabled === true;
  }

  ngOnDestroy() {
    this.projectDetailsService.filteredDailyReportIds.next([]);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
